import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Globe, 
  CheckCircle, 
  XCircle, 
  Clock,
  Search,
  Filter,
  X,
  Save
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { Base_Url } from '../config/domainConfig';
import './CountryMaster.css';

const API_BASE_URL = `${Base_Url}/api`;

const CountryMaster = () => {
  const { showSuccess, showError, showWarning } = useToast();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [formData, setFormData] = useState({
    countryname: '',
    countrycode: '',
    status: 'active'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch countries from API
  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/country-master`);
      const result = await response.json();
      
      if (result.success) {
        setCountries(result.data);
      } else {
        console.error('Failed to fetch countries:', result.message);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  // Filter countries based on search and status
  const filteredCountries = countries.filter(country => {
    const matchesSearch = country.countryname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         country.countrycode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || country.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.countryname.trim()) {
      errors.countryname = 'Country name is required';
    }
    
    if (!formData.countrycode.trim()) {
      errors.countrycode = 'Country code is required';
    } else if (formData.countrycode.length < 2 || formData.countrycode.length > 3) {
      errors.countrycode = 'Country code must be 2-3 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle add country
  const handleAddCountry = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/country-master`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowAddModal(false);
        setFormData({ countryname: '', countrycode: '', status: 'active' });
        setFormErrors({});
        fetchCountries();
        showSuccess('Country added successfully!');
      } else {
        showError(result.message || 'Failed to add country');
      }
    } catch (error) {
      console.error('Error adding country:', error);
      showError('Failed to add country');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit country
  const handleEditCountry = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/country-master/${selectedCountry.countryid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowEditModal(false);
        setSelectedCountry(null);
        setFormData({ countryname: '', countrycode: '', status: 'active' });
        setFormErrors({});
        fetchCountries();
        showSuccess('Country updated successfully!');
      } else {
        showError(result.message || 'Failed to update country');
      }
    } catch (error) {
      console.error('Error updating country:', error);
      showError('Failed to update country');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete country
  const handleDeleteCountry = async () => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/country-master/${selectedCountry.countryid}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowDeleteModal(false);
        setSelectedCountry(null);
        fetchCountries();
        showSuccess('Country deleted successfully!');
      } else {
        showError(result.message || 'Failed to delete country');
      }
    } catch (error) {
      console.error('Error deleting country:', error);
      showError('Failed to delete country');
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit modal
  const openEditModal = (country) => {
    setSelectedCountry(country);
    setFormData({
      countryname: country.countryname,
      countrycode: country.countrycode,
      status: country.status
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (country) => {
    setSelectedCountry(country);
    setShowDeleteModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({ countryname: '', countrycode: '', status: 'active' });
    setFormErrors({});
  };

  // Get status badge
  const getStatusBadge = (status) => {
    return (
      <span className={`status-badge ${status === 'active' ? 'success' : 'danger'}`}>
        {status === 'active' ? <CheckCircle size={14} /> : <XCircle size={14} />}
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="country-master">
        <div className="loading">Loading countries...</div>
      </div>
    );
  }

  return (
    <div className="country-master">
      {/* Header */}
      <div className="country-header">
        <div className="header-left">
          <h2>Country Master</h2>
          <p>Manage countries and their codes</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          <Plus size={16} />
          Add Country
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search countries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-box">
          <Filter size={18} />
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Countries Table */}
      <div className="table-container">
        <table className="country-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Country Name</th>
              <th>Country Code</th>
              <th>Status</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCountries.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  <Globe size={48} />
                  <p>No countries found</p>
                </td>
              </tr>
            ) : (
              filteredCountries.map(country => (
                <tr key={country.countryid}>
                  <td>{country.countryid}</td>
                  <td>{country.countryname}</td>
                  <td>
                    <span className="country-code">{country.countrycode}</span>
                  </td>
                  <td>{getStatusBadge(country.status)}</td>
                  <td>{new Date(country.created_at).toLocaleDateString()}</td>
                  <td>{new Date(country.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-icon" 
                        title="Edit"
                        onClick={() => openEditModal(country)}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn-icon danger" 
                        title="Delete"
                        onClick={() => openDeleteModal(country)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Country Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Country</h3>
              <button 
                className="btn-icon"
                onClick={() => setShowAddModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddCountry}>
              <div className="form-group">
                <label>Country Name *</label>
                <input
                  type="text"
                  name="countryname"
                  value={formData.countryname}
                  onChange={handleInputChange}
                  placeholder="Enter country name"
                  className={formErrors.countryname ? 'error' : ''}
                />
                {formErrors.countryname && (
                  <span className="error-message">{formErrors.countryname}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Country Code *</label>
                <input
                  type="text"
                  name="countrycode"
                  value={formData.countrycode}
                  onChange={handleInputChange}
                  placeholder="e.g., IND, USA"
                  maxLength={3}
                  className={formErrors.countrycode ? 'error' : ''}
                />
                {formErrors.countrycode && (
                  <span className="error-message">{formErrors.countrycode}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add Country'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Country Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Country</h3>
              <button 
                className="btn-icon"
                onClick={() => setShowEditModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleEditCountry}>
              <div className="form-group">
                <label>Country Name *</label>
                <input
                  type="text"
                  name="countryname"
                  value={formData.countryname}
                  onChange={handleInputChange}
                  placeholder="Enter country name"
                  className={formErrors.countryname ? 'error' : ''}
                />
                {formErrors.countryname && (
                  <span className="error-message">{formErrors.countryname}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Country Code *</label>
                <input
                  type="text"
                  name="countrycode"
                  value={formData.countrycode}
                  onChange={handleInputChange}
                  placeholder="e.g., IND, USA"
                  maxLength={3}
                  className={formErrors.countrycode ? 'error' : ''}
                />
                {formErrors.countrycode && (
                  <span className="error-message">{formErrors.countrycode}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Country'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Delete Country</h3>
              <button 
                className="btn-icon"
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="delete-content">
              {/* AlertCircle icon was removed from imports, so it's commented out or removed if not needed */}
              {/* <AlertCircle size={48} className="warning-icon" /> */}
              <h4>Are you sure you want to delete this country?</h4>
              <p>
                <strong>{selectedCountry?.countryname}</strong> ({selectedCountry?.countrycode})
              </p>
              <p className="warning-text">
                This action cannot be undone. All data associated with this country will be permanently deleted.
              </p>
            </div>
            
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-danger"
                onClick={handleDeleteCountry}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete Country'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryMaster; 